import CheckIcon from '@mui/icons-material/Check';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton, ListItemIcon, Menu } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { Dispatch, FC, MouseEvent, SetStateAction, useEffect, useState } from 'react';

import { CameraInfo } from '@apiContract';
import useLocalStorage from '@hooks/useLocalStorage';
import { useScreenOrientation } from '@hooks/useScreenOrientation';
import isUndefined from 'lodash/fp/isUndefined';

import { AutomatedCameraLocalStorageKeys } from '../../types';
import sx from './styles';

type Props = {
  cameraIndex?: number;
  setUserCamera: Dispatch<SetStateAction<CameraInfo>>;
  cameras?: CameraInfo[] | null;
};

const ChooseCameraButton: FC<Props> = ({ setUserCamera, cameras, cameraIndex }) => {
  const [storedCameraIndex, setStoredCameraIndex] = useLocalStorage<number | undefined>(
    AutomatedCameraLocalStorageKeys.UserCameraIndex,
    undefined,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const orientation = useScreenOrientation();

  useEffect(() => {
    if (cameras && cameras?.length > 0) {
      const matchedCamera = isUndefined(storedCameraIndex)
        ? cameras.find((camera) => camera.index === cameraIndex) || cameras[0]
        : cameras.find((camera) => camera.index === storedCameraIndex) || cameras[0];

      setUserCamera(matchedCamera);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeviceCameraClick = (camera: CameraInfo) => {
    setStoredCameraIndex(camera.index);
    setUserCamera(camera);
    handleClose();
  };

  return (
    <Box sx={sx.button(orientation)}>
      <IconButton
        aria-label="Choose camera"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <SettingsIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {cameras?.map((option) => {
          const isCurrentDevice = option.index === cameraIndex;
          return (
            <MenuItem
              key={option.name}
              selected={isCurrentDevice}
              onClick={() => handleDeviceCameraClick(option)}
              sx={!isCurrentDevice ? { pl: '44px' } : {}}
            >
              {isCurrentDevice && (
                <ListItemIcon sx={{ mr: 0 }}>
                  <CheckIcon />
                </ListItemIcon>
              )}
              {option.name}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default ChooseCameraButton;
